import { useRef, useState, useEffect } from "react";
import { Formik, Field, Form } from "formik";
import {
  InputGroup,
  FieldWrappers,
  Label,
  SubmitBtn,
  SideCancelBtn,
  CardWrapper,
  SelectionCard,
  Wrapper,
  ScrollableContent,
  ActionSection,
  RupeeIcon,
  MobileIcon,
  ModalHeader,
} from "../Styles/InputStyles";
import { toast } from "react-toastify";
import tw from "tailwind-styled-components";
import { MdClose, MdEdit } from "react-icons/md";
import * as Yup from "yup";
import { staff_roles } from "../../Config";
import MultiPropSelectModal from "../MultiPropSelectModal";
import SelectOptionModal from "../SelectOptionModal";

const AddStaff = ({
  addStaffLoading,
  addStaffMutate,
  setIsAddStaff,
  property,
  key,
}) => {
  const filteredProperties = property?.filter((prop) => prop.id !== 0) || [];
  const [height, setHeight] = useState(window.innerHeight);
  const [showPropertyModal, setShowPropertyModal] = useState(false);
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  let formikRef = useRef(null);

  useEffect(() => {
    if (formikRef.current) {
      formikRef.current.resetForm();
    }
    const handleResize = () => {
      setHeight(window.innerHeight); // updates when Safari UI changes
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [key]);

  const initialValues = {
    fullName: "",
    mobile: "",
    gender: "",
    salary: "",
    role: "",
  };

  const gender = [
    { value: 1, label: "Male" },
    { value: 2, label: "Female" },
  ];

  const staffRoleOptions = staff_roles.map((role) => ({
    label: role.label,
    value: role.value,
  }));

  const validationSchema = Yup.object({
    fullName: Yup.string()
      .required("Name is required")
      .matches(
        /^[A-Za-z0-9.\s]+$/,
        "Only letters, numbers, spaces and dot are allowed.",
      )
      .min(4, "Name must be at least 4 characters long"),
    mobile: Yup.string()
      .required("Mobile number is required")
      .matches(/^[0-9]{10}$/, "Mobile number must be exactly 10 digits"),
    gender: Yup.number().required("Gender is required"),
  });

  const handleSubmit = (values, { resetForm }) => {
    const nameRegex = /^[A-Za-z0-9. ]{4,}$/;
    const mobileRegex = /^\d{10}$/;
    const salaryRegex = /^\d{1,7}$/;
    const maxSalaryDeposit = 1000000;

    if (!nameRegex.test(values.fullName)) {
      toast.error("Only letters, numbers, spaces and dot are allowed.");
      return;
    }

    if (!mobileRegex.test(values.mobile)) {
      toast.error(
        "Mobile number should be exactly 10 digits long and contain only numbers",
      );
      return;
    }

    if (!values.gender) {
      toast.error("Gender is required");
      return;
    }
    if (
      (values.salary && !salaryRegex.test(values.salary)) ||
      parseInt(values.salary) > maxSalaryDeposit
    ) {
      toast.error(
        "Salary must be less than 10,00,000 and contain only numbers",
      );
      return;
    }

    addStaffMutate({
      name: values.fullName,
      mobile: values.mobile,
      gender: values.gender,
      salary: Number(values.salary),
      role: values.role || "",
    });
    //resetForm();
  };

  const handleSelectedProperties = (selectedProperties) => {
    if (formikRef.current) {
      formikRef.current.setFieldValue("properties", selectedProperties);
    }
  };

  const handlePropertySave = (properties) => {
    handleSelectedProperties(properties);
    setShowPropertyModal(false);
  };

  return (
    <Container
      style={{
        height: `${height}px`,
        overflow: "auto",
        WebkitOverflowScrolling: "touch",
      }}
    >
      <ModalHeader>
        <h1 className="text-white text-lg font-medium">Add Staff</h1>
        <button onClick={() => setIsAddStaff(false)}>
          <MdClose color="white" size={24} />
        </button>
      </ModalHeader>

      <div className="px-6 h-full flex flex-col justify-between">
        <Formik
          initialValues={initialValues}
          innerRef={formikRef}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, setFieldValue, resetForm, errors, touched }) => {
            const handleRoleSelect = (selectedValue) => {
              setFieldValue("role", selectedValue);
            };
            const getSelectedStaffRole = () => {
              if (!values.role) return "Select Staff Role";

              const selected = staffRoleOptions.find(
                (s) => s.value === values.role,
              );

              return selected ? selected.label : "Select Staff Role";
            };

            return (
              <Form>
                <ScrollableContent className="max-h-[calc(100vh-140px)]">
                  <Wrapper>
                    <InputGroup>
                      <Label htmlFor="fullName">Staff Name *</Label>
                      <FieldWrappers>
                        <Field
                          type="text"
                          name="fullName"
                          id="fullName"
                          autoComplete="off"
                          placeholder="Enter staff name"
                        />
                      </FieldWrappers>
                      {errors.fullName && touched.fullName && (
                        <ErrorText>{errors.fullName}</ErrorText>
                      )}
                    </InputGroup>
                    <InputGroup>
                      <Label htmlFor="mobile">Mobile Number *</Label>
                      <FieldWrappers>
                        <MobileIcon />
                        <Field
                          type="text"
                          name="mobile"
                          id="mobile"
                          inputMode="numeric"
                          autoComplete="off"
                          className="pl-5"
                          placeholder="Enter 10-digit mobile number"
                          maxLength={10}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (/^\d{0,10}$/.test(value)) {
                              setFieldValue("mobile", value);
                            }
                          }}
                        />
                      </FieldWrappers>
                      {errors.mobile && touched.mobile && (
                        <ErrorText>{errors.mobile}</ErrorText>
                      )}
                    </InputGroup>

                    <InputGroup>
                      <Label>Gender *</Label>
                      <CardWrapper>
                        {gender.map((mode) => (
                          <SelectionCard
                            key={mode.value}
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              setFieldValue("gender", mode.value, false);
                            }}
                            className={
                              values.gender === mode.value
                                ? "bg-[#26A69A] text-white"
                                : "bg-gray-100 text-gray-600"
                            }
                          >
                            <span className="text-sm">{mode.label}</span>
                          </SelectionCard>
                        ))}
                      </CardWrapper>
                      {errors.gender && touched.gender && (
                        <ErrorText>{errors.gender}</ErrorText>
                      )}
                    </InputGroup>

                    <InputGroup>
                      <Label htmlFor="salary">Monthly Salary </Label>
                      <FieldWrappers>
                        <RupeeIcon />
                        <Field
                          type="number"
                          name="salary"
                          id="salary"
                          autoComplete="off"
                          className="w-full pl-5"
                          placeholder="Enter monthly salary (optional)"
                        />
                      </FieldWrappers>
                    </InputGroup>

                    {/* <InputGroup>
                      <Label>Staff Role</Label>

                      <FieldWrappers
                        onClick={() => setIsRoleModalOpen(true)}
                        // className="border border-gray-400"
                      >
                        <Field
                          type="text"
                          readOnly
                          value={getSelectedStaffRole()}
                          className="w-full bg-transparent border-none outline-none cursor-pointer"
                        />

                        <MdEdit className="w-4 h-4 text-gray-400 hover:text-gray-600 transition-colors duration-200 flex-shrink-0 mr-2" />
                      </FieldWrappers>
                      {touched.role && errors.role && (
                        <div className="min-h-[20px] mt-1">
                          <p className="text-rose-600 text-sm">
                            {errors.role}
                          </p>
                        </div>
                      )}
                    </InputGroup> */}
                  </Wrapper>
                </ScrollableContent>
                <ActionSection>
                  <SideCancelBtn
                    type="button"
                    onClick={() => {
                      setIsAddStaff(false);
                      resetForm();
                    }}
                  >
                    Cancel
                  </SideCancelBtn>
                  <SubmitBtn type="submit" disabled={addStaffLoading}>
                    {addStaffLoading ? "Submiting..." : "Submit"}
                  </SubmitBtn>
                </ActionSection>

                {/* Property Selection Modal */}
                <MultiPropSelectModal
                  isOpen={showPropertyModal}
                  onClose={() => setShowPropertyModal(false)}
                  onSelect={handlePropertySave}
                  options={filteredProperties}
                  selectedProperties={values.properties}
                />

                {/* Staff Role Selection Modal */}
                <SelectOptionModal
                  isOpen={isRoleModalOpen}
                  onClose={() => setIsRoleModalOpen(false)}
                  title="Select Staff Role"
                  options={staffRoleOptions}
                  selectedValue={values.role}
                  onSelect={handleRoleSelect}
                  searchPlaceholder="Search staff roles..."
                />
              </Form>
            );
          }}
        </Formik>
      </div>
    </Container>
  );
};

const Container = tw.div`bg-white w-full h-full flex flex-col relative`;
const ErrorText = tw.div`text-red-500 text-sm ml-0.5`;

export default AddStaff;
